import styled, { css, keyframes } from "styled-components";
import Chip from "@mui/material/Chip";

const heartbeatPulse = keyframes`
    0%,
    100% {
        transform: scale(1);
    }
    15% {
        transform: scale(1.08);
    }
    30% {
        transform: scale(0.98);
    }
    45% {
        transform: scale(1.05);
    }
    60% {
        transform: scale(1);
    }
`;

export const StyledFooterWrapper = styled.div`
    display: flex;
    padding: 1em 0 0;
    position: relative;
    width: 100%;
    box-sizing: border-box;
    overflow-x: hidden;
`;

export const StyledActions = styled.div(
    ({ theme }) => css`
        display: flex;
        flex-wrap: wrap;
        color: ${theme.palette.common.black};
        font-family: ${theme.typography.base.main};
        justify-content: space-between;
        list-style: none;
        width: 100%;
        z-index: 0;
        padding-bottom: 8px;
        min-height: 48px;
        align-items: center;
        gap: 8px;

        & > li {
            min-width: 0;
        }
        &:before {
            height: 3px;
            content: "";
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            z-index: -1;
            border-radius: inherit;
            background: rgb(68, 220, 255);
            background: -moz-linear-gradient(
                90deg,
                rgba(250, 47, 92, 1) 0%,
                rgba(202, 88, 239, 1) 50%,
                rgba(68, 220, 255, 1) 100%
            );
            background: -webkit-linear-gradient(
                90deg,
                rgba(250, 47, 92, 1) 0%,
                rgba(202, 88, 239, 1) 50%,
                rgba(68, 220, 255, 1) 100%
            );
            background: linear-gradient(
                90deg,
                rgba(250, 47, 92, 1) 0%,
                rgba(202, 88, 239, 1) 50%,
                rgba(68, 220, 255, 1) 100%
            );
            filter: progid:DXImageTransform.Microsoft.gradient(startColorstr="#f63167",endColorstr="#50cfff",GradientType=1);
        }
    `
);

export const StyledActionsLeft = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    & > li {
        display: flex;
        align-items: center;
        gap: 6px;
        list-style: none;
        min-width: 0;
    }
`;

export const StyledActionItem = styled.li`
    min-width: 0;
`;

export const CommentsContainer = styled.div<{ isExpanded: boolean }>`
    overflow: hidden;
    max-height: ${({ isExpanded }) => (isExpanded ? "400px" : "0")};
    opacity: ${({ isExpanded }) => (isExpanded ? "1" : "0")};
    transition: max-height 0.3s ease-in-out, opacity 0.3s ease-in-out;

    ${({ isExpanded }) =>
        isExpanded &&
        `
        overflow-y: auto; // Scrollable content
        padding-right: 8px; // Optional: To accommodate scrollbars nicely
    `}
`;

export const UndoCountdownWrapper = styled.div(
    ({ theme }) => css`
        display: inline-flex;
        flex-direction: row;
        align-items: center;
        gap: 6px;
        color: ${theme.palette.common.blue};
        font-family: Arial, sans-serif;
        font-size: 0.85rem;
        font-weight: 400;
        margin-left: 4px;
        position: relative;
    `
);

export const UndoCountdownLegend = styled.span(
    ({ theme }) => css`
        order: 1;
        color: ${theme.palette.common.darkGray};
        font-family: Arial, sans-serif;
        font-size: 0.75rem;
        font-weight: 700;
        line-height: 1.2;
        text-transform: uppercase;
        text-align: left;
        display: inline-flex;
        flex-direction: row;
        align-items: center;
        gap: 4px;
        white-space: nowrap;
    `
);

export const UndoCountdownAnimation = styled.div`
    order: 0;
    width: 34px;
    height: 34px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

export const UndoCountdownBadge = styled.div(
    ({ theme }) => css`
        width: 30px;
        height: 30px;
        border-radius: 999px;
        border: 2px solid ${theme.palette.common.purple};
        background: ${theme.palette.common.white};
        display: flex;
        align-items: center;
        justify-content: center;
        color: ${theme.palette.common.darkGray};
        font-family: Arial, sans-serif;
        font-size: 0.95rem;
        font-weight: 400;
        line-height: 1;
        transform-origin: center;
        animation: ${heartbeatPulse} 1s ease-in-out infinite;

        @media (prefers-reduced-motion: reduce) {
            animation: none;
        }
    `
);

export const UndoCountdownBadgeValue = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
`;

export const UndoCountdownChip = styled(Chip)(
    ({ theme }) => css`
        height: 34px;
        border-radius: 999px;
        border: 1px solid ${theme.palette.common.lightGray};
        background: ${theme.palette.common.white};
        color: ${theme.palette.common.darkGray};
        font-family: Arial, sans-serif;

        .MuiChip-label {
            padding-left: 6px;
            padding-right: 12px;
            font-size: 0.75rem;
            font-weight: 400;
            text-transform: none;
            letter-spacing: normal;
            white-space: nowrap;
        }

        .MuiChip-avatar {
            width: 24px;
            height: 24px;
            margin-left: 4px;
            margin-right: 2px;
            border: 0;
            color: ${theme.palette.common.white};
            background: ${theme.palette.common.purple};
            font-family: Arial, sans-serif;
            font-size: 0.72rem;
            font-weight: 700;
            line-height: 1;
            animation: ${heartbeatPulse} 1s ease-in-out infinite;

            @media (prefers-reduced-motion: reduce) {
                animation: none;
            }
        }
    `
);

export const UndoCountdownText = styled.span`
    border: 0;
    clip: rect(0 0 0 0);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    white-space: nowrap;
    width: 1px;
`;
